// UpNextWeekCards — 2x2 grid of small white deliverable cards on the program
// dashboard's left column. Each card surfaces an in-flight or in-review
// deliverable with a status chip, due date, serif title, optional lead, and
// a thin progress bar at the bottom. Click navigates to the deliverables
// tab focused on that row.

import { View } from "react-native";

import { StatusCard } from "../../aspects/StatusCard";
import { PANE_THEMES } from "../../brand/paneThemes";

const STUDENT_THEME = PANE_THEMES.Student;

import type {
  Deliverable,
  TeamDeliverableStatusRow,
  TeamSubmissionRow,
} from "../../../types/database";


type Variant = "active" | "in-review";

type Card = {
  id: string;
  title: string;
  variant: Variant;
  dueLabel: string;
  /** 0..1 progress estimate. */
  progress: number;
};

export type UpNextWeekCardsProps = {
  deliverables: Deliverable[];
  submissions: TeamSubmissionRow[];
  statuses: TeamDeliverableStatusRow[];
  onPressDeliverable: (deliverableId: string) => void;
  /** Override "now" for tests. */
  now?: number;
};

function fmtShort(iso: string | null): string {
  if (!iso) return "TBD";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "TBD";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function progressFromDates(
  opensAt: string | null,
  dueAt: string | null,
  now: number
): number {
  if (!opensAt || !dueAt) return 0.05;
  const o = new Date(opensAt).getTime();
  const d = new Date(dueAt).getTime();
  if (Number.isNaN(o) || Number.isNaN(d) || d <= o) return 0.5;
  const span = d - o;
  return Math.max(0.05, Math.min(0.95, (now - o) / span));
}

export function UpNextWeekCards({
  deliverables,
  submissions,
  statuses,
  onPressDeliverable,
  now: nowOverride,
}: UpNextWeekCardsProps) {
  const now = nowOverride ?? Date.now();
  const submittedIds = new Set(submissions.map((s) => s.deliverable_id));
  const statusById = new Map<string, TeamDeliverableStatusRow>();
  for (const s of statuses) statusById.set(s.deliverable_id, s);

  const sorted = [...deliverables].sort(
    (a, b) => (a.sequence ?? 0) - (b.sequence ?? 0)
  );

  const cards: Card[] = [];
  for (const d of sorted) {
    if (cards.length >= 4) break;
    const submitted = submittedIds.has(d.id);
    const stat = statusById.get(d.id);
    const isLocked =
      d.opens_at != null && new Date(d.opens_at).getTime() > now;

    if (submitted && stat?.status === "pending") {
      cards.push({
        id: d.id,
        title: d.name,
        variant: "in-review",
        dueLabel: fmtShort(d.due_at),
        progress: 1,
      });
      continue;
    }
    if (!submitted && !isLocked) {
      cards.push({
        id: d.id,
        title: d.name,
        variant: "active",
        dueLabel: fmtShort(d.due_at),
        progress: progressFromDates(d.opens_at, d.due_at, now),
      });
    }
  }

  if (cards.length === 0) return null;

  return (
    <View
      style={{
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 12,
      }}
    >
      {cards.map((c) => (
        <View key={c.id} style={{ flexBasis: "48%", flexGrow: 1, minWidth: 220 }}>
          <StatusCard
            theme={STUDENT_THEME}
            tone={c.variant === "in-review" ? "accent" : "success"}
            chipLabel={c.variant === "in-review" ? "In review" : "Active"}
            dateLabel={c.dueLabel}
            title={c.title}
            progress={c.progress}
            onPress={() => onPressDeliverable(c.id)}
            accessibilityLabel={`Open deliverable: ${c.title}`}
          />
        </View>
      ))}
    </View>
  );
}

